import React from 'react';
import { Calendar, MapPin, ExternalLink, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HERO } from '@/constants/testIds';
import { AAWTA } from '@/lib/aawta';

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

export const Hero = () => {
  return (
    <section
      id="home"
      className="relative pt-28 pb-20 md:pt-32 md:pb-28 overflow-hidden bg-aawta-navy text-white"
    >
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-aawta-navy via-aawta-navy/95 to-aawta-navy-deep/95" />
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-aawta-magenta/25 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-aawta-magenta/15 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-aawta-magenta animate-pulse" />
              <span className="overline text-white/90">Inaugural Summit · September 17, 2026</span>
            </div>

            <h1 className="font-display font-bold mt-6 text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl leading-[1.05] tracking-tight">
              Partner with <span className="text-aawta-magenta">AAWTA</span>.<br />
              Shape the Future Workforce.
            </h1>

            <p className="mt-6 text-lg md:text-xl text-white/85 max-w-xl leading-relaxed">
              A powerful one-day summit convening education, employers, workforce agencies,
              higher education, and community leaders to build the transportation, logistics,
              and supply chain workforce of tomorrow.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-3 text-white/90">
              <div className="inline-flex items-center gap-2">
                <Calendar className="h-4.5 w-4.5 text-aawta-magenta" strokeWidth={2} />
                <span className="font-medium">{AAWTA.eventDate}</span>
                <span className="text-white/50">·</span>
                <span className="font-medium">{AAWTA.eventTime}</span>
              </div>
              <div className="inline-flex items-center gap-2">
                <MapPin className="h-4.5 w-4.5 text-aawta-magenta" strokeWidth={2} />
                <span className="font-medium">{AAWTA.eventLocation}</span>
              </div>
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                data-testid={HERO.becomeSponsor}
                onClick={() => openExternal(AAWTA.zeffySponsorUrl)}
                className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-7 py-6 text-[15px] shadow-aawta-magenta hover:-translate-y-0.5 transition-all duration-200 group"
              >
                Become a Sponsor
                <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                data-testid={HERO.registerNow}
                onClick={() => openExternal(AAWTA.eventbriteUrl)}
                variant="outline"
                className="rounded-full bg-white text-aawta-navy hover:bg-white/90 border-white font-medium px-7 py-6 text-[15px] group"
              >
                Register Now
                <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                data-testid={HERO.downloadFlyer}
                onClick={() => openExternal(AAWTA.flyerUrl)}
                variant="ghost"
                className="rounded-full bg-transparent text-white hover:bg-white/10 border border-white/30 hover:border-white/60 font-medium px-6 py-6 text-[15px]"
              >
                <Download className="mr-2 h-4 w-4" />
                Event Flyer
              </Button>
            </div>

            <div className="mt-12 flex items-center gap-6 text-white/70 text-sm">
              <div>
                <div className="font-display font-bold text-2xl text-white">175+</div>
                <div className="text-white/60">Regional Leaders</div>
              </div>
              <div className="w-px h-10 bg-white/15" />
              <div>
                <div className="font-display font-bold text-2xl text-white">8</div>
                <div className="text-white/60">Sectors Represented</div>
              </div>
              <div className="w-px h-10 bg-white/15" />
              <div>
                <div className="font-display font-bold text-2xl text-white">1</div>
                <div className="text-white/60">Regional Movement</div>
              </div>
            </div>
          </div>

          {/* Featured event flyer image */}
          <div className="lg:col-span-6 reveal" style={{ animationDelay: '150ms' }}>
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-br from-aawta-magenta/40 to-aawta-magenta/10 blur-3xl rounded-3xl" />
              <div className="relative rounded-3xl overflow-hidden border-2 border-white/15 shadow-[0_30px_80px_rgba(0,0,0,0.4)] bg-aawta-navy-deep">
                <img
                  src={AAWTA.flyerUrl}
                  alt="AAWTA Education & Industry Workforce Collaborative — Event Flyer"
                  data-testid={HERO.flyerImage}
                  className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-700"
                  loading="eager"
                />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white text-aawta-navy text-[11px] font-bold tracking-[0.18em] uppercase shadow-aawta-elevated whitespace-nowrap">
                Building Pathways · Creating Opportunities
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
